import axios from 'axios';
import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LoadingButton } from '@routes-plan/ui';
import StandardModal from '../../../components/modales/StandardModal';
import { useT } from '../../../i18n';
import { formatDay, plural } from '../storage/StorageCleanup';

/**
 * Confirmar «Eliminar cliente». Se borran sus datos personales, fotos y documentos; sus préstamos y abonos
 * quedan como «Cliente eliminado». Solo clientes sin saldo. Se puede recuperar durante 30 días (Papelera).
 */
interface Props {
  ids: number[];
  open: boolean;
  onClose: () => void;
  /** Al terminar: mensaje para el usuario y si se eliminó alguno. */
  onDone: (message: string, severity: 'success' | 'warning' | 'error', erasedIds: number[]) => void;
}

type Result = { erased: { id: number }[]; blocked: { id: number; name: string; reason: string }[]; restoreUntil: string | null };

const ClientEraseModal: React.FC<Props> = ({ ids, open, onClose, onDone }) => {
  const t = useT();
  const [busy, setBusy] = useState(false);

  const erase = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('/clients/bulk-delete', { ids });
      const r = data.data as Result;
      const balance = r.blocked.filter((b) => b.reason === 'BALANCE').length;
      const done = t(plural('clients.erase.done', r.erased.length), { count: r.erased.length, date: formatDay(r.restoreUntil) });
      onDone(balance ? `${done} ${t(plural('clients.erase.blockedBalance', balance), { count: balance })}` : done, balance ? 'warning' : 'success', r.erased.map((e) => e.id));
    } catch (error: any) {
      const code = error?.response?.data?.code;
      onDone(code === 'CLIENT_HAS_BALANCE' ? t(plural('clients.erase.blockedBalance', ids.length), { count: ids.length }) : t('common.deleteError'), code === 'CLIENT_HAS_BALANCE' ? 'warning' : 'error', []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <StandardModal
      open={open}
      onClose={() => !busy && onClose()}
      title={t(plural('clients.erase.title', ids.length), { count: ids.length })}
      maxWidth={560}
      disableClose={busy}
      actions={
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={busy} color="inherit">
            {t('common.cancel')}
          </Button>
          <LoadingButton variant="contained" color="error" onClick={erase} loading={busy}>
            {t('clients.erase.confirm')}
          </LoadingButton>
        </Stack>
      }
    >
      <Stack spacing={1.5}>
        <Typography>{t('clients.erase.body1')}</Typography>
        <Typography>{t('clients.erase.body2')}</Typography>
        <Typography color="text.secondary" variant="body2">
          {t('clients.erase.body3')}
        </Typography>
      </Stack>
    </StandardModal>
  );
};

export default ClientEraseModal;
